import { useTranslation } from 'react-i18next'

import { NotepadIcon } from '@phosphor-icons/react'

import { ComboBox, Divider, InputWithLabel, LineItem, Text } from '@reveni-io/reveni-ui'

import { isValidBarcode, isValidHSCode } from '@/inventory/pages/InventoryPage/helpers'

import CountryComboBox from '../CountryComboBox/CountryComboBox'
import { TitleContainer } from '../styles'
import PidLabel from './PidLabel/PidLabel'
import { weightUnits } from './constants'
import { getMissingCustomsFields, getPlatformName } from './helpers'
import * as Styles from './styles'

const ItemInfo = ({
  item,
  product,
  regions,
  onChange,
  currency,
  needsNsPid,
  provider,
  shipsWithCustoms,
  customsCountries,
  applyInfoToAllVariants,
}) => {
  const { t } = useTranslation()

  const platform = getPlatformName(provider)

  const handleOnChangeCountry = e => {
    onChange(
      'country',
      regions?.find(region => region.code === e.target.value)
    )
  }

  const handleOnChangeBarcode = e => onChange('barcode', e.target.value)

  const handleOnChangeNsPid = e => onChange('ns_pid', e.target.value)

  const handleOnChangeHSCode = e => onChange('hs_code', e.target.value || null)

  const handleOnChangeUnitCost = e => onChange('unit_cost', e.target.value || null)

  const handleOnChangeWeight = e => onChange('weight', e.target.value || null)

  const handleOnChangeWeightUnit = e => onChange('weight_unit', e.target.value)

  const handleOnChangeCustomsDescription = e => onChange('customs_description', e.target.value)

  const showHSCodeError = () => item?.hs_code && !isValidHSCode(item.hs_code)

  const showBarcodeError = () => !applyInfoToAllVariants && item?.barcode && !isValidBarcode(item.barcode)

  const getBarcodeHelperText = () => {
    if (applyInfoToAllVariants) return t('No se aplica a todas las variantes')
    if (showBarcodeError()) return t('El código de barras debe tener al menos 8 dígitos')

    return t('Único por variante')
  }

  const missingCustomsFields = getMissingCustomsFields({ item, shipsWithCustoms })

  return (
    <Styles.Container>
      <Styles.Section>
        <TitleContainer>
          <NotepadIcon size={24} />
          <Text type="bodyMBold">{t('Detalles del producto')}</Text>
        </TitleContainer>
        <LineItem
          key={item?.id}
          item={{ ...item, product: { image_url: item?.image_url || product?.image_url, name: item?.title } }}
          options={{
            showStatus: false,
            showReason: false,
            showActionType: false,
          }}
        />
      </Styles.Section>
      <Styles.Section>
        <Text type="bodyMBold">{t('Identificadores de producto')}</Text>
        <Styles.FieldsContainer>
          <InputWithLabel
            label={needsNsPid ? <PidLabel label={t('SKU')} pid={t('M-PID')} /> : t('SKU')}
            value={item?.sku || ''}
            disabled
            fullWidth
            helperText={platform && t('Sincronizado desde {{platform}}', { platform })}
          />
          <InputWithLabel
            label={needsNsPid ? <PidLabel label={t('Código de barras')} pid={t('S-PID')} /> : t('Código de barras')}
            value={item?.barcode || ''}
            onChange={handleOnChangeBarcode}
            disabled={applyInfoToAllVariants}
            fullWidth
            error={showBarcodeError()}
            helperText={getBarcodeHelperText()}
          />
        </Styles.FieldsContainer>
        {needsNsPid && (
          <InputWithLabel
            label={t('NS-PID')}
            value={item?.ns_pid || ''}
            onChange={handleOnChangeNsPid}
            disabled={applyInfoToAllVariants}
            fullWidth
            helperText={applyInfoToAllVariants ? t('No se aplica a todas las variantes') : undefined}
          />
        )}
        <InputWithLabel
          label={t('HS Code')}
          value={item?.hs_code || ''}
          onChange={handleOnChangeHSCode}
          fullWidth
          error={showHSCodeError() || missingCustomsFields.hs_code}
          InputLabelProps={{
            error: !!showHSCodeError(),
            focused: missingCustomsFields.hs_code ? false : undefined,
          }}
          helperText={
            showHSCodeError()
              ? t('El HS Code es inválido.')
              : missingCustomsFields.hs_code &&
                t('La aduana no puede clasificar este producto. Los envíos a {{country}} pueden quedar retenidos.', {
                  country: customsCountries,
                })
          }
        />
      </Styles.Section>
      <Divider />
      <Styles.Section>
        <Text type="bodyMBold">{t('Origen')}</Text>
        <CountryComboBox
          label={t('País de origen')}
          value={item?.country?.code}
          handleChange={handleOnChangeCountry}
          menuItems={regions?.map(region => ({
            value: region.code,
            label: region.name,
          }))}
          fullWidth
          error={missingCustomsFields.country}
          helperText={
            missingCustomsFields.country
              ? t(
                  'La aduana no puede aplicar los aranceles correctos. Los envíos pueden quedar retenidos o tener cargos extra.'
                )
              : undefined
          }
        />
      </Styles.Section>
      <Divider />
      <Styles.Section>
        <Text type="bodyMBold">{t('Logística')}</Text>
        <InputWithLabel
          label={t('Coste unitario')}
          type="number"
          value={Number(item?.unit_cost) || ''}
          onChange={handleOnChangeUnitCost}
          endAdornment={currency}
          fullWidth
          error={missingCustomsFields.unit_cost}
          InputLabelProps={{ error: false, focused: missingCustomsFields.unit_cost ? false : undefined }}
          helperText={
            missingCustomsFields.unit_cost &&
            t('No se puede declarar el valor en aduana. Los envíos pueden quedar retenidos.')
          }
        />
        <Styles.FieldsContainer>
          <InputWithLabel
            label={t('Peso')}
            type="number"
            value={Number(item?.weight) || ''}
            onChange={handleOnChangeWeight}
            fullWidth
            error={missingCustomsFields.weight}
            InputLabelProps={{ error: false, focused: missingCustomsFields.weight ? false : undefined }}
            helperText={
              missingCustomsFields.weight &&
              t('Los envíos no se pueden declarar por completo y pueden retrasarse en aduana.')
            }
          />
          <ComboBox
            value={item?.weight_unit}
            handleChange={handleOnChangeWeightUnit}
            menuItems={weightUnits.map(unit => ({
              label: t(unit.label),
              value: unit.value,
            }))}
            fullWidth
          />
        </Styles.FieldsContainer>
        <InputWithLabel
          label={t('Descripción para aduanas')}
          value={item?.customs_description || ''}
          onChange={handleOnChangeCustomsDescription}
          maxLength={50}
          fullWidth
        />
      </Styles.Section>
    </Styles.Container>
  )
}

export default ItemInfo
